import { useState } from "react";

import { uploadFinalDecisionDocumentFile } from "@synthesis/shared-services";
import { useCreateRemark } from "@synthesis/shared-services/internal/application/hooks";
import { Button, Modal, setPromiseSnack, TextArea } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import { Uploader, type UploaderListItem } from "../../../../components/uploader";
import { useUserInfo } from "../../../../shared/hooks/use-user-info";
import {
  FINAL_DECISION_REMARK_CREATE_SNACK_DESCRIPTION,
  FINAL_DECISION_REMARK_CREATE_SNACK_MESSAGES,
  invalidateFinalDecisionVersionQueries,
} from "./final-decision.lib";

type FinalDecisionRemarkModalProps = {
  requestId: string;
  onClose: () => void;
};

type FinalDecisionRemarkFormValues = {
  comment: string;
};

export const FinalDecisionRemarkModal = ({ requestId, onClose }: FinalDecisionRemarkModalProps) => {
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const createRemarkMutation = useCreateRemark({ mutation: { mutationKey: ["final-decision-remark-create", requestId] } });

  const methods = useForm<FinalDecisionRemarkFormValues>({
    defaultValues: { comment: "" },
    mode: "onChange",
  });

  const comment = methods.watch("comment");
  const isSubmitDisabled = isSaving || !comment.trim();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleSelectFiles = (files: File[]) => {
    if (isSaving) {
      return;
    }

    setSelectedFile(files[0] ?? null);
  };

  const handleDeleteFromList = (_fileMeta: UploaderListItem) => {
    setSelectedFile(null);
  };

  const handleSubmit = methods.handleSubmit(async (formData) => {
    if (!userInfo) {
      return;
    }

    setIsSaving(true);

    const promise = (async () => {
      const documentIds: string[] = [];

      if (selectedFile) {
        const fileMeta = await uploadFinalDecisionDocumentFile({ requestId, filename: selectedFile.name, userId: userInfo.id }, selectedFile);

        if (!fileMeta.id) {
          throw new Error("API не вернул идентификатор файла");
        }

        documentIds.push(fileMeta.id);
      }

      await createRemarkMutation.mutateAsync({
        path: { requestId },
        body: {
          comment: formData.comment.trim(),
          ...(documentIds.length > 0 ? { documentIds } : {}),
        },
      });

      await invalidateFinalDecisionVersionQueries(queryClient, requestId);
      onClose();
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: FINAL_DECISION_REMARK_CREATE_SNACK_MESSAGES,
        description: FINAL_DECISION_REMARK_CREATE_SNACK_DESCRIPTION,
      },
    });

    promise
      .catch(() => undefined)
      .finally(() => {
        setIsSaving(false);
      });
  });

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Замечание к решению</Modal.Title>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-6 p-8">
              <FormProvider {...methods}>
                <div>
                  <h3 className="heading-h4 after:text-type-error after:ml-1 after:content-['*']">Замечание</h3>
                  <p className="paragraph-base text-type-minor mt-2">Опишите замечания к итоговому решению</p>
                  <div className="mt-4">
                    <TextArea name="comment" rows={5} placeholder="Опишите замечания" disabled={isSaving} />
                  </div>
                </div>
              </FormProvider>

              <Uploader
                multiple={false}
                resetBeforeUpload
                isViewFileList
                allowDeleteFromList
                onSelectFiles={handleSelectFiles}
                onDeleteFromList={handleDeleteFromList}
              />
            </div>
          </div>

          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
            <Button type="submit" theme="primary" size="lg" disabled={isSubmitDisabled}>
              Отправить
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isSaving} onClick={() => handleOpenChange(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};
